/**
 * ExpelMkIISkillEffect — Poison tree Expel path evolution, replaces Expel.
 *
 * Evolved poison aura: 2-tile radius (was 1.5), 50% faster DoT tick rate
 * (333ms vs the ZoneSystem default 500ms), reduced toxinLoad drain (5.0/sec
 * vs 6.67, ~20s uptime), and survives one hit above the 20% HP threshold
 * before dropping on the second (breakTolerance: 1).
 *
 * New mechanic: each DoT tick applies a brief 200ms slow (intensity 0.1)
 * to every enemy inside the aura — a soft pulse rather than persistent
 * zone-wide deceleration. The game layer calls handleZoneTick() with each
 * dot_tick event to trigger the slow application.
 *
 * Upgrade inheritance: resolver queries use 'expel' scope (via replacesId
 * chain) so Membrane's auraRadiusBonus, Footwork's zone speed modifier,
 * and Absorption's HP recovery all apply automatically.
 *
 * Extends ExpelSkillEffect with evolved defaults and a deps wrapper that
 * injects the faster tick interval into zone creation.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Expel Mk.II (expel_evolved)
 */

import { ExpelSkillEffect } from './ExpelSkillEffect.js';
import type { ExpelDeps, ExpelConfig } from './ExpelSkillEffect.js';
import type { ZoneConfig } from '../../../systems/ZoneSystem.js';
import type { StackingRule } from '../../../systems/StatusEffectSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ExpelMkIIDeps extends ExpelDeps {
  readonly statusEffects: {
    applyToEntity(entityId: string, defId: string, opts: {
      sourceSkillId: string;
      intensity: number;
      durationMs: number;
      stackingRuleOverride?: StackingRule;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ExpelMkIIConfig extends ExpelConfig {
  /** Zone tick interval in ms (50% faster than the 500ms default). */
  readonly tickIntervalMs: number;
  /** Slow intensity applied per tick to enemies in the aura. */
  readonly slowIntensity: number;
  /** Slow duration in ms per tick application. */
  readonly slowDurationMs: number;
}

const MK2_DEFAULTS: ExpelMkIIConfig = {
  cooldownMs: 1200,                // medium cooldown tier (same as base)
  baseRadiusTiles: 2,              // was 1.5
  dotIntensity: 3,                 // same per-tick intensity
  dotDurationMs: 4000,             // same DoT duration
  toxinLoadMax: 100,               // same pool
  toxinDrainPerSecond: 5.0,        // reduced from 6.67 (~20s uptime)
  toxinRechargePerSecond: 10,      // same recharge rate
  hpBreakThreshold: 0.2,          // same 20% threshold
  breakTolerance: 1,               // survives one hit (was 0)
  tickIntervalMs: 667,             // 50% faster than 500ms default
  slowIntensity: 0.1,             // 10% slow per tick
  slowDurationMs: 200,             // 200ms brief pulse
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ExpelMkIISkillEffect extends ExpelSkillEffect {
  override readonly skillId = 'expel_evolved';

  private readonly _statusEffectDeps: ExpelMkIIDeps['statusEffects'];
  private readonly _tickIntervalMs: number;
  private readonly _slowIntensity: number;
  private readonly _slowDurationMs: number;

  constructor(deps: ExpelMkIIDeps, config: Partial<ExpelMkIIConfig> = {}) {
    const fullConfig = { ...MK2_DEFAULTS, ...config };
    const tickIntervalMs = fullConfig.tickIntervalMs;

    // Wrap the zoneSystem to inject tickIntervalMs into every zone creation
    const wrappedDeps: ExpelDeps = {
      zoneSystem: {
        create(zoneConfig: ZoneConfig): string {
          return deps.zoneSystem.create({
            ...zoneConfig,
            tickIntervalMs,
          });
        },
        remove(zoneId: string): void {
          deps.zoneSystem.remove(zoneId);
        },
        getZone(zoneId: string) {
          return deps.zoneSystem.getZone(zoneId);
        },
      },
      resolver: deps.resolver,
      player: deps.player,
    };

    super(wrappedDeps, fullConfig);

    this._statusEffectDeps = deps.statusEffects;
    this._tickIntervalMs = fullConfig.tickIntervalMs;
    this._slowIntensity = fullConfig.slowIntensity;
    this._slowDurationMs = fullConfig.slowDurationMs;
  }

  /**
   * Called by the game layer for each dot_tick zone event while the aura is
   * active. Applies a brief slow to the affected enemy.
   *
   * @param entityId The entity that received the DoT tick.
   */
  handleZoneTick(entityId: string): void {
    if (!this._isActive) return;

    this._statusEffectDeps.applyToEntity(entityId, 'slow', {
      sourceSkillId: 'expel_evolved',
      intensity: this._slowIntensity,
      durationMs: this._slowDurationMs,
      stackingRuleOverride: 'override',
    });
  }

  // --- Config getters for test assertions ---

  get tickIntervalMs(): number { return this._tickIntervalMs; }
  get slowIntensity(): number { return this._slowIntensity; }
  get slowDurationMs(): number { return this._slowDurationMs; }
}
